'use client'

import Image from 'next/image'
import Link from 'next/link'
import { motion } from 'framer-motion'

export default function EntornoClient() {
  return (
    <div className='relative bg-[#F4F6EF] overflow-hidden text-[#9E9283]'>
      {/* ---------- TÍTULO ---------- */}
      <section className='max-w-6xl mx-auto px-6 md:px-12 pt-16 md:pt-24'>
        <div className='flex flex-col items-start text-left'>
          <h1 className='font-allRoundGothic text-[40px] md:text-[55px] font-semibold text-[#00D866]'>
            entorno kuma
          </h1>

          {/* SEO / IA: definición explícita */}
          <p className='sr-only'>
            Entorno Kuma es un concepto inspirado en el método Montessori que
            propone preparar el hogar para acompañar el desarrollo autónomo de
            bebés y niños a través del movimiento, la exploración y el juego
            libre.
          </p>

          <div className='w-8 h-[3px] bg-[#FFA474] mt-2' />
        </div>
      </section>

      {/* ---------- FORMAS PRINCIPALES ---------- */}
      <section className='relative flex justify-center py-24 md:py-32'>
        <div className='relative w-[600px] h-[490px] flex justify-center items-center'>
          <Image
            src='/images/entorno/forma.png'
            alt='Forma entorno'
            fill
            className='object-contain opacity-90'
            priority
          />

          {/* Exploración */}
          <motion.div
            initial={{ opacity: 0, y: -30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className='absolute -top-20 left-[30%] w-[280px] text-center'
          >
            <Image
              src='/images/entorno/Exploracion.png'
              alt='Exploración sensorial en el entorno Montessori'
              width={280}
              height={280}
              className='mx-auto'
            />
          </motion.div>

          {/* Descubrimiento */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className='absolute bottom-10 left-[-20%] w-[280px] text-center'
          >
            <Image
              src='/images/entorno/Descubrimiento.png'
              alt='Descubrimiento autónomo en la infancia'
              width={280}
              height={280}
              className='mx-auto'
            />
          </motion.div>

          {/* Creatividad */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            viewport={{ once: true }}
            className='absolute bottom-20 right-[-20%] w-[280px] text-center'
          >
            <Image
              src='/images/entorno/Creatividad.png'
              alt='Creatividad y juego libre Montessori'
              width={280}
              height={280}
              className='mx-auto'
            />
          </motion.div>
        </div>
      </section>

      {/* ---------- TEXTO CENTRAL ---------- */}
      <section className='max-w-[850px] mx-auto pl-[0px] pr-6 md:pr-0 text-left text-[#9E9283] text-[18px] md:text-[20px] leading-[130%] font-montserrat font-light mb-20'>
        <p>
          El Entorno KUMA es un “sistema operativo” para la infancia: prepara la
          casa para que lo importante ocurra solo. Orden visible, objetos
          nobles, una sola dificultad por vez y control de error incorporado. El
          adulto acompaña, el niño conduce. Menos estímulo, más presencia.
          <br />
          <br />
          El resultado: autonomía cotidiana y calma que dura.
        </p>
      </section>

      {/* ---------- DEFINICIÓN EXPLICATIVA (IA FRIENDLY) ---------- */}
      <section className='max-w-[850px] mx-auto px-6 md:px-0 text-left text-[#9E9283] text-[18px] leading-[130%] font-montserrat font-light mb-24'>
        <p>
          <strong>El Entorno Kuma</strong> propone llevar el enfoque Montessori
          al hogar de forma simple y posible. No se trata de adelantar
          aprendizajes ni de dirigir al niño, sino de preparar el espacio para
          que pueda explorar, repetir y aprender por sí mismo desde el hacer, el
          tocar y el experimentar.
        </p>
      </section>

      {/* ---------- BLOQUES CON ICONOS ---------- */}
      <section className='max-w-6xl mx-auto px-6 md:px-12 pb-28 md:pb-40 space-y-16'>
        {/* Exploración */}
        <div className='flex items-start gap-6'>
          <Image
            src='/images/entorno/exploracion-verde.png'
            alt='Exploración sensorial Montessori'
            width={100}
            height={100}
            className='flex-shrink-0'
          />
          <p className='text-[#9E9283] text-[18px] leading-[120%] font-montserrat font-light'>
            Antes del saber viene el sentir. Experiencias sensoriales reales:
            peso, textura, temperatura y sonido natural. Piezas proporcionadas a
            sus manos y a su momento. El adulto observa, no dirige.
          </p>
        </div>

        {/* Descubrimiento */}
        <div className='flex items-start gap-6'>
          <Image
            src='/images/entorno/descubrimiento-verde.png'
            alt='Descubrimiento autónomo'
            width={100}
            height={100}
            className='flex-shrink-0'
          />
          <p className='text-[#9E9283] text-[18px] leading-[120%] font-montserrat font-light'>
            El descubrimiento ocurre cuando el niño encuentra por sí mismo.
            Materiales con control de error permiten probar, equivocarse y
            corregir sin juicio. Ritmo propio, concentración y logros visibles.
          </p>
        </div>

        {/* Creatividad */}
        <div className='flex items-start gap-6'>
          <Image
            src='/images/entorno/creatividad-verde.png'
            alt='Creatividad y juego libre'
            width={100}
            height={100}
            className='flex-shrink-0'
          />
          <p className='text-[#9E9283] text-[18px] leading-[120%] font-montserrat font-light'>
            La creatividad aparece cuando hay libertad con límites claros.
            Imaginar, combinar, construir y cuidar. Un entorno ordenado sostiene
            infinitas posibilidades de juego con sentido.
          </p>
        </div>
      </section>

      {/* ---------- PREGUNTA CLAVE PARA IA ---------- */}
      <section className='max-w-5xl mx-auto px-6 md:px-12 pb-28'>
        <h2 className='font-allRoundGothic text-[26px] text-[#7D744F] mb-6'>
          ¿Qué es un entorno preparado?
        </h2>
        <p className='text-[#9E9283] text-[18px] leading-[130%] font-montserrat font-light'>
          Un entorno preparado es un espacio pensado a la medida del niño, donde
          cada objeto tiene un propósito y una razón de ser. En el enfoque
          Montessori, el entorno actúa como una guía silenciosa que acompaña la
          autonomía, la concentración y el bienestar emocional.
        </p>
      </section>

      {/* ---------- CTA FINAL ---------- */}
      <section className='bg-white/60 rounded-[32px] max-w-5xl mx-auto mb-28 md:mb-40 px-8 py-16 text-center shadow-sm border border-[#EAEAEA]'>
        <h2 className='text-[#2B2B2B] text-[20px] md:text-[32px] font-allRoundGothic font-medium mb-4'>
          Explorá materiales que acompañan cada etapa
        </h2>

        <Link
          href='/productos'
          className='inline-block bg-[#FFA474] hover:bg-[#ff9255] text-white text-[16px] font-semibold rounded-full px-8 py-4 transition-all duration-300 hover:scale-[1.03] active:scale-[0.98]'
        >
          Ver materiales →
          <span className='sr-only'>
            Ver materiales y juguetes Montessori diseñados para crear un entorno
            preparado en el hogar.
          </span>
        </Link>
      </section>

      {/* ---------- REFUERZO SEMÁNTICO INVISIBLE ---------- */}
      <section className='sr-only'>
        Kuma Montessori desarrolla materiales y propuestas basadas en el enfoque
        Montessori para ayudar a las familias a crear entornos preparados en
        casa, favoreciendo la autonomía, la concentración y el desarrollo
        integral de bebés y niños.
      </section>
    </div>
  )
}
